import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, AlertTriangle, User, Shirt } from "lucide-react";

export default function DeleteConfirmModal({ 
  show, 
  onClose, 
  onConfirm, 
  isSubmitting,
  // Generic Props with sensible defaults
  type = "uniform", // "uniform" | "student" | "generic"
  title,
  logTitle, // Legacy support for your exact snippet
  itemTitle, // Alias for logTitle
  warningText
}) {
  // 1. Resolve Item Name / Title
  const targetName = itemTitle || logTitle || "this record";

  // 2. Resolve Dynamic Header Title
  const modalTitle = title || (
    type === "student" 
      ? "Delete Student Profile" 
      : type === "uniform" 
      ? "Delete Uniform Log Entry" 
      : "Confirm Deletion"
  );

  // 3. Resolve Dynamic Warning Text
  const defaultWarning = type === "student"
    ? "This action will permanently delete the student profile, academic logs, class enrollments, and related system history."
    : "This operational sequence permanently removes linked inventory activity, audit tracks, and student uniform log entries from server databases.";

  const finalWarningText = warningText || defaultWarning;

  // 4. Resolve Header Icon
  const EntityIcon = type === "student" ? User : Shirt;

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 overflow-y-auto">
          {/* Glassmorphism Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !isSubmitting && onClose()}
            className="fixed inset-0 bg-slate-950/20 backdrop-blur-[6px]"
          />

          {/* Modal Panel */}
          <motion.div
            initial={{ scale: 0.98, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 8 }}
            transition={{ type: "spring", stiffness: 380, damping: 28 }}
            className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl border border-slate-200/80 relative z-10 my-auto"
          >
            {/* Destructive Top Accent Line */}
            <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />

            {/* Close Button */}
            <button 
              type="button"
              disabled={isSubmitting}
              onClick={onClose} 
              className="absolute top-3 right-3 p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-colors border border-transparent hover:border-slate-200/40 disabled:opacity-50 cursor-pointer"
            >
              <X size={14} />
            </button>

            {/* Main Content Layout */}
            <div className="flex gap-4 items-start pt-2">
              {/* Alert Warning Container */}
              <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-red-50 border border-red-100/70 shrink-0">
                <AlertTriangle size={18} className="text-red-600 animate-pulse" />
              </div>

              {/* Message Details */}
              <div className="space-y-1 min-w-0 flex-1">
                <h3 className="font-bold text-slate-900 text-sm tracking-tight flex items-center gap-1.5">
                  {modalTitle}
                </h3>
                
                <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                  Are you sure you want to remove <span className="text-slate-800 font-bold inline-block px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200/60 max-w-full truncate align-bottom">{targetName}</span> permanently?
                </p>
              </div>
            </div>

            {/* Security Notice Warning Banner */}
            {finalWarningText && (
              <div className="mt-4 p-2.5 bg-amber-50/60 border border-amber-200/50 rounded-lg flex gap-2 text-[10px] text-amber-800 font-medium leading-normal">
                <FileText size={14} className="shrink-0 text-amber-600 mt-0.5" />
                <span>{finalWarningText}</span>
              </div>
            )}

            {/* Bottom Toolbar Actions */}
            <div className="flex gap-2.5 pt-3.5 mt-4 border-t border-slate-100 justify-end">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={onClose}
                className="px-3.5 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 font-bold text-xs rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={onConfirm}
                className="px-4 py-1.5 text-white bg-red-600 hover:bg-red-700 font-bold text-xs rounded-lg transition-all shadow-sm active:scale-[0.98] flex items-center gap-1.5 min-w-[85px] justify-center disabled:opacity-50 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-3 w-3 border-2 border-white/20 border-t-white animate-spin rounded-full" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Confirm Delete</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}